/**
 * Componente para subir documentos/fotos con preview rectangular
 * Ideal para fotos de documentos de identidad, soportes de EPS, etc.
 */

import { useState, useRef, useCallback } from "react";
import { Upload, Loader2, X, FileText, Image, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { validateImageFile } from "@/lib/services/storage";
import { EncryptedDocumentView } from "@/components/ui/EncryptedDocumentView";
import type { EncryptionMeta } from "@/lib/services/crypto";
import { toast } from "react-hot-toast";

interface DocumentUploadProps {
  /** URL del documento actual */
  currentUrl?: string | null;
  /** Título del documento */
  title: string;
  /** Descripción o hint */
  description?: string;
  /** Icono a mostrar cuando no hay documento */
  icon?: React.ReactNode;
  /** Callback cuando se sube un archivo exitosamente */
  onUpload: (file: File) => Promise<{ url: string; path: string }>;
  /** Callback cuando se elimina el documento */
  onRemove?: () => void;
  /** Deshabilitar la edición */
  disabled?: boolean;
  /** Acepta solo imágenes o también PDFs */
  acceptPdf?: boolean;
  /** Clase CSS adicional */
  className?: string;
  /** Si está en proceso de carga */
  isUploading?: boolean;
  /** Metadata de encriptación (si el archivo está cifrado AES-256-GCM) */
  encryption?: EncryptionMeta | null;
  /** Content-type original (para PDFs cifrados o desencriptación correcta) */
  contentType?: string;
}

export function DocumentUpload({
  currentUrl,
  title,
  description,
  icon,
  onUpload,
  onRemove,
  disabled = false,
  acceptPdf = false,
  className,
  isUploading: externalUploading,
  encryption,
  contentType,
}: DocumentUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploading = externalUploading || isUploading;
  const displayUrl = previewUrl || currentUrl;

  const acceptedTypes = acceptPdf
    ? "image/jpeg,image/png,image/webp,application/pdf"
    : "image/jpeg,image/png,image/webp";

  const handleFileSelect = useCallback(
    async (file: File) => {
      // Validar si es imagen
      if (file.type.startsWith("image/")) {
        const validation = validateImageFile(file);
        if (!validation.valid) {
          toast.error(validation.error || "Archivo no válido");
          return;
        }
      } else if (file.type === "application/pdf") {
        if (!acceptPdf) {
          toast.error("Solo se permiten imágenes");
          return;
        }
        // Validar tamaño del PDF (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error("El archivo no puede superar los 10MB");
          return;
        }
      } else {
        toast.error("Tipo de archivo no soportado");
        return;
      }

      // Materializar el archivo en memoria de inmediato.
      // En móvil, el File que devuelve el selector es solo una referencia al
      // archivo en disco; si el usuario tarda en enviar el formulario, el SO
      // puede invalidar esa referencia y la lectura posterior (resize/subida)
      // falla con NotReadableError ("The requested file could not be read…").
      // Leer los bytes ahora, con el handle fresco, deja una copia estable.
      let safeFile = file;
      try {
        const buffer = await file.arrayBuffer();
        safeFile = new File([buffer], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
      } catch (err) {
        console.error("No se pudo leer el archivo seleccionado:", err);
        toast.error(
          "No se pudo leer el archivo. Vuelve a seleccionarlo e inténtalo de nuevo.",
        );
        return;
      }

      // Mostrar preview si es imagen
      if (safeFile.type.startsWith("image/")) {
        const objectUrl = URL.createObjectURL(safeFile);
        setPreviewUrl(objectUrl);
      }

      // Subir archivo
      setIsUploading(true);
      try {
        await onUpload(safeFile);
        toast.success(`${title} subido correctamente`);
      } catch (error) {
        console.error("Error uploading document:", error);
        toast.error(`Error al subir ${title.toLowerCase()}`);
        setPreviewUrl(null);
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload, acceptPdf, title],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    e.target.value = "";
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (disabled || uploading) return;

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [disabled, uploading, handleFileSelect],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !uploading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove();
      setPreviewUrl(null);
    }
  };

  const handleClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  const handleViewDocument = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (displayUrl) {
      window.open(displayUrl, "_blank");
    }
  };

  const isPdf =
    displayUrl?.includes(".pdf") ||
    displayUrl?.includes("application/pdf") ||
    contentType === "application/pdf";
  // Solo aplicamos desencriptación cuando el archivo viene de Storage (currentUrl)
  // y tenemos clave. El previewUrl es un objectURL local del File recién elegido.
  const showEncrypted = !previewUrl && currentUrl && encryption;

  return (
    <div className={cn("relative", className)}>
      {/* Input oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled || uploading}
      />

      {/* Área de upload */}
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          "relative border-2 border-dashed rounded-xl p-4 transition-all duration-300",
          "flex flex-col items-center justify-center min-h-[140px]",
          isDragging && "border-primary-500 bg-primary-50 scale-[1.02]",
          disabled && "cursor-not-allowed opacity-60 bg-gray-50",
          !disabled &&
            !uploading &&
            "cursor-pointer hover:border-primary-400 hover:bg-gray-50",
          displayUrl ? "border-green-300 bg-green-50/50" : "border-gray-300",
        )}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            <span className="text-sm text-gray-500">Subiendo...</span>
          </div>
        ) : displayUrl ? (
          <div className="w-full">
            {showEncrypted ? (
              <div className="relative">
                <EncryptedDocumentView
                  url={currentUrl}
                  encryption={encryption}
                  contentType={contentType}
                  alt={title}
                  className="w-full h-32 object-contain rounded-lg"
                  isPdf={isPdf}
                />
              </div>
            ) : isPdf ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-8 h-8 text-red-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  PDF subido
                </span>
              </div>
            ) : (
              <div className="relative">
                <img
                  src={displayUrl}
                  alt={title}
                  className="w-full h-32 object-contain rounded-lg"
                />
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex justify-center gap-2 mt-3">
              <button
                type="button"
                onClick={handleViewDocument}
                className="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors flex items-center gap-1"
              >
                <Eye className="w-3.5 h-3.5" />
                Ver
              </button>
              {onRemove && (
                <button
                  type="button"
                  onClick={handleRemove}
                  className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1"
                >
                  <X className="w-3.5 h-3.5" />
                  Eliminar
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
              {icon || <Image className="w-6 h-6 text-gray-400" />}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">{title}</p>
              {description && (
                <p className="text-xs text-gray-500 mt-0.5">{description}</p>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-primary-600 font-medium">
              <Upload className="w-3.5 h-3.5" />
              <span>Haz clic o arrastra</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentUpload;
